/* ============================================================
   StatsReel.jsx (5th iteration — SOUND FAMILIAR + 4 cinematic
   stat slides + CTA, founder mockups 2026-02)
   ------------------------------------------------------------
   Slide order (6 total):
     1. SOUND FAMILIAR? — personal story opener
        ("They said: 'Everything looks normal.'
          You knew: Something was wrong.
          You were right.")
     2. RUSHED CARE        — 18 min (Ganguli JAMA IM 2023)
     3. GHOSTED RESULTS    — 18.1% (Murphy BMJ Q&S 2019)
     4. BROKEN LOOP        — 1 in 14 (Singh JPS 2020)
     5. SYSTEM FAILURE     — 12.1% (Singh Diagnosis Berlin 2025)
     6. CTA — CADENCE Medical Consulting fills that Gap.

   Each stat slide uses the new cinematic 4-line structure:
     • Bracketed category tag in upper-left
     • Big WHITE all-caps headline (full sentence with the stat)
     • Copper/foil-gold emphasis line (the gut-punch)
     • White description body (the why / mechanism)
     • Parenthetical journal citation
   ============================================================ */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import '../styles/StatsReel.css';

const DWELL_MS = 7000;
// After a manual tap, pause auto-advance for this long so the reader can
// linger on the slide they intentionally landed on. Resumes silently after.
const ENGAGE_PAUSE_MS = 8000;

const StatsReel = ({
  soundFamiliar,
  stats,
  cta,
  ctas, // optional: array of multiple CTA slides (e.g. demo options to A/B/C compare)
  testIdPrefix = 'stats-cascade',
}) => {
  // Compose the unified slide list. Supports either a single `cta` or
  // multiple `ctas` (rendered as consecutive closing slides).
  const ctaSlides = Array.isArray(ctas) && ctas.length > 0
    ? ctas.map((c) => ({ type: 'cta', ...c }))
    : cta ? [{ type: 'cta', ...cta }] : [];
  const slides = [
    soundFamiliar ? { type: 'sound-familiar', ...soundFamiliar } : null,
    ...stats.map((s) => ({ type: 'stat', ...s })),
    ...ctaSlides,
  ].filter(Boolean);

  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef(null);
  const [started, setStarted] = useState(false);
  // engageUntil is the timestamp until which auto-advance is paused.
  const [engageUntil, setEngageUntil] = useState(0);
  // One-time "swipe me" affordance nudge the first time the reel is seen.
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const swipeHintDoneRef = useRef(false);

  // Honor reduced-motion only — mobile gets the reel too.
  const [isStaticMode, setIsStaticMode] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setIsStaticMode(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else mq.removeListener(update);
    };
  }, []);
  const prefersReducedMotion = isStaticMode;

  useEffect(() => {
    if (prefersReducedMotion) {
      const id = setTimeout(() => setStarted(true), 0);
      return () => clearTimeout(id);
    }
    const node = sectionRef.current;
    if (!node) return undefined;
    // AUTOPLAY DELAY (2026-02 founder spec: "the slides start playing
    // before you can get to scroll and you end up missing the 1st
    // slide. Delay autoplay until the reel is fully in view.")
    // No kickoff timeout — autoplay starts only when the reel itself
    // is sufficiently scrolled into the viewport. Threshold 0.55 so
    // at least half the reel must be visible before slide 1 begins.
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        obs.disconnect();
        setStarted(true);
      },
      { threshold: 0.55 },
    );
    obs.observe(node);
    return () => {
      obs.disconnect();
    };
  }, [prefersReducedMotion]);

  // One-shot swipe affordance: a subtle horizontal nudge of the active
  // slide the first time the reel is in view, so phone visitors discover
  // they can swipe. Mobile-only (gated in CSS), once per mount, skipped
  // under reduced-motion.
  useEffect(() => {
    if (!started || prefersReducedMotion || swipeHintDoneRef.current) return undefined;
    swipeHintDoneRef.current = true;
    const onId = setTimeout(() => setShowSwipeHint(true), 1500);
    const offId = setTimeout(() => setShowSwipeHint(false), 1500 + 1500);
    return () => {
      clearTimeout(onId);
      clearTimeout(offId);
    };
  }, [started, prefersReducedMotion]);

  useEffect(() => {
    if (!started || prefersReducedMotion) return undefined;
    // Sticky final slide — once the visitor lands on the closing CTA, hold
    // there forever. The CTA is the conversion moment; do not let it scroll
    // past. Visitor can still tap to wrap back to the first slide manually.
    if (activeIndex === slides.length - 1) return undefined;
    const now = Date.now();
    // Per-slide dwell override (slide.dwellMs) wins so short-text slides
    // (e.g. "Doctors want to help / 15 mins won't") can advance faster.
    const slideDwell = slides[activeIndex] && slides[activeIndex].dwellMs;
    const baseDwell = slideDwell || DWELL_MS;
    // If the visitor recently interacted, hold the current slide for the
    // remainder of the engage-pause window before resuming auto-advance.
    const delay = engageUntil > now ? engageUntil - now : baseDwell;
    const t = setTimeout(() => {
      setActiveIndex((i) => (i + 1) % slides.length);
    }, delay);
    return () => clearTimeout(t);
  }, [activeIndex, started, slides, prefersReducedMotion, engageUntil]);

  const advance = useCallback(
    (e) => {
      // If this click is the tail end of a horizontal swipe, the swipe
      // handler already navigated — swallow the synthetic tap.
      if (swipeHandledRef.current) {
        swipeHandledRef.current = false;
        return;
      }
      if (
        e &&
        e.target &&
        e.target.closest('a, button.stats-cascade-cta-btn')
      ) {
        return;
      }
      // Mark engagement so auto-advance pauses for ENGAGE_PAUSE_MS before
      // silently resuming.  Lets readers linger on a slide they tapped to.
      setEngageUntil(Date.now() + ENGAGE_PAUSE_MS);
      setActiveIndex((i) => (i + 1) % slides.length);
    },
    [slides.length],
  );

  // ---------- Swipe navigation (mobile) ----------
  // Phone users instinctively swipe; layer left/right swipe on top of the
  // existing tap-to-advance + auto-timer. Vertical scrolling is untouched
  // (we never preventDefault), so the page still scrolls normally.
  const touchStartRef = useRef(null);
  const swipeHandledRef = useRef(false);
  const SWIPE_THRESHOLD = 45;

  const goNext = useCallback(() => {
    setEngageUntil(Date.now() + ENGAGE_PAUSE_MS);
    setActiveIndex((i) => (i + 1) % slides.length);
  }, [slides.length]);

  const goPrev = useCallback(() => {
    setEngageUntil(Date.now() + ENGAGE_PAUSE_MS);
    setActiveIndex((i) => (i - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const handleTouchStart = useCallback((e) => {
    const t = e.touches && e.touches[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  }, []);

  const handleTouchEnd = useCallback(
    (e) => {
      const start = touchStartRef.current;
      touchStartRef.current = null;
      const t = e.changedTouches && e.changedTouches[0];
      if (!start || !t) return;
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      // Only treat as a swipe when the gesture is clearly horizontal —
      // otherwise it's a vertical scroll and should be left alone.
      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.3) {
        swipeHandledRef.current = true;
        if (dx < 0) goNext();
        else goPrev();
      }
    },
    [goNext, goPrev],
  );

  /* ---------- Slide renderers ---------- */
  const renderBridge = (slide, i) => {
    if (slide.image) {
      return (
        <div
          key={`bridge-${i}`}
          className={`stats-reel-slide stats-reel-slide-image ${i === activeIndex || prefersReducedMotion ? 'is-active' : ''}`}
          data-testid={`${testIdPrefix}-slide-bridge`}
          aria-hidden={i !== activeIndex && !prefersReducedMotion}
        >
          <img src={slide.image} alt="Doctors want to help" className="stats-reel-slide-img" />
        </div>
      );
    }
    return (
    <div
      key={`bridge-${i}`}
      className={`stats-reel-slide stats-reel-slide-bridge ${i === activeIndex || prefersReducedMotion ? 'is-active' : ''}`}
      data-testid={`${testIdPrefix}-slide-bridge`}
      aria-hidden={i !== activeIndex && !prefersReducedMotion}
    >
      <div className="stats-reel-slide-card stats-reel-slide-card-bridge">
        <h3 className="bridge-headline">{slide.headline}</h3>
        {slide.subtitle && (
          <p className="bridge-subtitle">{slide.subtitle}</p>
        )}
      </div>
    </div>
  );
  };

  const renderSoundFamiliar = (slide, i) => {
    if (slide.image) {
      return (
        <div
          key={`sf-${i}`}
          className={`stats-reel-slide stats-reel-slide-image ${i === activeIndex || prefersReducedMotion ? 'is-active' : ''}`}
          data-testid={`${testIdPrefix}-slide-sf`}
          aria-hidden={i !== activeIndex && !prefersReducedMotion}
        >
          <img src={slide.image} alt="Sound Familiar?" className="stats-reel-slide-img" />
        </div>
      );
    }
    return (
    <div
      key={`sf-${i}`}
      className={`stats-reel-slide stats-reel-slide-sf ${i === activeIndex || prefersReducedMotion ? 'is-active' : ''}`}
      data-testid={`${testIdPrefix}-slide-sf`}
      aria-hidden={i !== activeIndex && !prefersReducedMotion}
    >
      <div className="stats-reel-slide-card stats-reel-slide-card-sf">
        <h3 className="sound-familiar-heading">{slide.heading}</h3>
        {slide.quote && (
          <p className="sound-familiar-quote">{slide.quote}</p>
        )}
        {slide.realization && (
          <p className="sound-familiar-realization">{slide.realization}</p>
        )}
        {slide.affirmation && (
          <p className="sound-familiar-affirmation">{slide.affirmation}</p>
        )}
      </div>
    </div>
  );
  };

  const renderStat = (slide, i) => {
    if (slide.image) {
      return (
        <div
          key={slide.id || `stat-${i}`}
          className={`stats-reel-slide stats-reel-slide-image ${i === activeIndex || prefersReducedMotion ? 'is-active' : ''}`}
          data-testid={`${testIdPrefix}-slide-${slide.id || i}`}
          aria-hidden={i !== activeIndex && !prefersReducedMotion}
        >
          <img src={slide.image} alt={slide.alt || slide.category || 'Stat slide'} className="stats-reel-slide-img" />
        </div>
      );
    }
    return (
    <div
      key={slide.id || `stat-${i}`}
      className={`stats-reel-slide stats-reel-slide-stat ${i === activeIndex || prefersReducedMotion ? 'is-active' : ''}`}
      data-testid={`${testIdPrefix}-slide-${slide.id || i}`}
      aria-hidden={i !== activeIndex && !prefersReducedMotion}
    >
      <article className="stats-reel-slide-card stats-reel-slide-card-stat">
        {slide.category && (
          <span className="stats-reel-category-tag">{slide.category}</span>
        )}
        {slide.headline && (
          <h3 className="stats-reel-stat-headline">{slide.headline}</h3>
        )}
        {slide.emphasis && (
          <p className="stats-reel-stat-emphasis">{slide.emphasis}</p>
        )}
        {slide.body && (
          <p className="stats-reel-stat-body">{slide.body}</p>
        )}
        {slide.source && (
          <p className="stats-reel-source" data-testid={`${testIdPrefix}-source-${slide.id}`}>
            ({slide.source})
          </p>
        )}
      </article>
    </div>
  );
  };

  const renderCta = (slide, i) => {
    // Image-backed CTA with content OVERLAID on top of the slide image.
    if (slide.image) {
      const buttonLabel = slide.button && slide.button.label;
      const buttonHref = slide.button && slide.button.href;
      const isInternal = buttonHref && buttonHref.startsWith('/');
      const btnClass = slide.goldButton
        ? 'stats-cascade-cta-btn stats-cascade-cta-btn--gold'
        : 'stats-cascade-cta-btn';
      return (
        <div
          key={`cta-${i}`}
          className={`stats-reel-slide stats-reel-slide-image stats-reel-slide-image-cta ${slide.transparent ? 'is-floating' : ''} ${i === activeIndex || prefersReducedMotion ? 'is-active' : ''}`}
          data-testid={`${testIdPrefix}-cta-${slide.demoKey || i}`}
          aria-hidden={i !== activeIndex && !prefersReducedMotion}
        >
          {slide.demoLabel && (
            <span className="stats-reel-demo-badge" aria-hidden="true">
              {slide.demoLabel}
            </span>
          )}
          <img
            src={slide.image}
            alt={slide.headline || 'CADENCE Medical Consulting'}
            className="stats-reel-slide-img"
          />
          {(slide.headline || slide.body || buttonLabel) && (
            <div className="stats-reel-image-cta-overlay">
              {slide.headline && (
                <h2 className="stats-reel-image-cta-headline">{slide.headline}</h2>
              )}
              {slide.body && (
                <p className="stats-reel-image-cta-body">
                  {Array.isArray(slide.body)
                    ? slide.body.map((line, idx) => <span key={idx}>{line}</span>)
                    : slide.body}
                </p>
              )}
              {buttonLabel && buttonHref && (
                isInternal ? (
                  <Link
                    to={buttonHref}
                    className={btnClass}
                    data-testid={`${testIdPrefix}-cta-btn-${slide.demoKey || i}`}
                  >
                    {buttonLabel}
                    <ArrowRight size={16} />
                  </Link>
                ) : (
                  <a
                    href={buttonHref}
                    className={btnClass}
                    data-testid={`${testIdPrefix}-cta-btn-${slide.demoKey || i}`}
                  >
                    {buttonLabel}
                    <ArrowRight size={16} />
                  </a>
                )
              )}
            </div>
          )}
        </div>
      );
    }
    return (
    <div
      key={`cta-${i}`}
      className={`stats-reel-slide stats-reel-slide-cta ${i === activeIndex || prefersReducedMotion ? 'is-active' : ''}`}
      data-testid={`${testIdPrefix}-cta`}
      aria-hidden={i !== activeIndex && !prefersReducedMotion}
    >
      <div className="stats-reel-slide-card stats-cascade-cta-card">
        {slide.tagline && (
          <p className="stats-cascade-cta-tagline">
            {slide.tagline.split('|').map((line, idx) => (
              <span key={idx} className="stats-cascade-cta-tagline-line">
                {line.trim()}
              </span>
            ))}
          </p>
        )}
        {slide.headline && (
          <h2 className="stats-cascade-cta-headline">{slide.headline}</h2>
        )}
        {slide.eyebrow && (
          <p className="stats-cascade-cta-eyebrow">{slide.eyebrow}</p>
        )}
        {slide.body && (
          <p className="stats-cascade-cta-body">
            {Array.isArray(slide.body)
              ? slide.body.map((line, idx) => <span key={idx}>{line}</span>)
              : slide.body}
          </p>
        )}
        {slide.button && slide.button.label && slide.button.href && (
          slide.button.href.startsWith('/') ? (
            <Link
              to={slide.button.href}
              className="stats-cascade-cta-btn"
              data-testid={`${testIdPrefix}-cta-btn`}
            >
              {slide.button.label}
              <ArrowRight size={16} />
            </Link>
          ) : (
            <a
              href={slide.button.href}
              className="stats-cascade-cta-btn"
              data-testid={`${testIdPrefix}-cta-btn`}
            >
              {slide.button.label}
              <ArrowRight size={16} />
            </a>
          )
        )}
      </div>
    </div>
  );
  };

  const renderSlide = (slide, i) => {
    if (slide.type === 'sound-familiar') return renderSoundFamiliar(slide, i);
    if (slide.type === 'bridge') return renderBridge(slide, i);
    if (slide.type === 'cta') return renderCta(slide, i);
    return renderStat(slide, i);
  };

  return (
    <div
      ref={sectionRef}
      className={`stats-cascade ${prefersReducedMotion ? 'is-static' : ''}`}
      data-testid={testIdPrefix}
    >
      {prefersReducedMotion ? (
        <div className="stats-reel-static-stack">{slides.map(renderSlide)}</div>
      ) : (
        <>
          <button
            type="button"
            className={`stats-reel-stage stats-reel-stage-unified ${showSwipeHint ? 'show-swipe-hint' : ''}`}
            onClick={advance}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            aria-label="Advance to next slide"
            data-testid={`${testIdPrefix}-stage`}
          >
            {/* REEL №1 foil-stamped tag — sits in the bottom-left
                corner of the brass marquee like the cue mark on an
                old projection print. Visible on prior slides only;
                fades when the closing puzzle slide arrives so the
                puzzle floats free. (2026-02 founder: "like the gold
                stamp REEL №1 idea".) */}
            {started && activeIndex < slides.length - 1 && (
              <span
                className="stats-reel-reel-stamp"
                aria-hidden="true"
                data-testid={`${testIdPrefix}-reel-stamp`}
              >
                REEL №1
              </span>
            )}
            {/* PEEKABOO PAPER DECK — fans LEFT behind the final CTA
                slide. Now renders ACTUAL thumbnails of the previous
                slides (founder spec 2026-02: "way more exaggerated
                shuffle — I didn't want the edges purple like a
                cartoon, I wanted to actually show the edges of the
                slides like a real paper deck"). Up to the 3 slides
                immediately before the active one, fanned with
                progressively heavier rotation + offset so the reader
                sees real artwork edges, not a coloured tab. */}
            {started && activeIndex === slides.length - 1 && (
              <div
                className="stats-reel-peekaboo stats-reel-peekaboo--left"
                aria-hidden="true"
                data-testid={`${testIdPrefix}-peekaboo`}
              >
                {slides
                  .slice(Math.max(0, activeIndex - 3), activeIndex)
                  .map((s, idx, arr) => {
                    // Position relative to the stack: idx 0 is the deepest
                    // peek (back of the deck), idx arr.length-1 is the one
                    // closest to the active slide. Reverse so DOM order
                    // mirrors visual stacking depth.
                    const depth = arr.length - 1 - idx; // 0 = closest, 2 = deepest
                    return (
                      <span
                        key={`peek-${activeIndex - depth - 1}`}
                        className={`stats-reel-peekaboo-card stats-reel-peekaboo-card--depth-${depth}`}
                      >
                        {s.image ? (
                          <img
                            src={s.image}
                            alt=""
                            className="stats-reel-peekaboo-img"
                            draggable="false"
                          />
                        ) : null}
                      </span>
                    );
                  })}
              </div>
            )}
            {slides.map(renderSlide)}
          </button>
          {/* GOLD REPLAY MEDALLION — only surfaces when the reel has
              reached its sticky final slide. Tap to wrap back to the
              first slide and restart the show. */}
          {started && activeIndex === slides.length - 1 && (
            <button
              type="button"
              className="stats-reel-replay"
              onClick={() => {
                setEngageUntil(0);
                setActiveIndex(0);
              }}
              aria-label="Replay the show from the beginning"
              data-testid={`${testIdPrefix}-replay`}
            >
              <svg className="stats-reel-replay-glyph" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 12a9 9 0 1 0 3-6.7"
                />
                <polyline
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  points="3 3 3 9 9 9"
                />
              </svg>
            </button>
          )}
          <span className="stats-carousel-progress" role="tablist" aria-label="Reel slide navigation">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={i === activeIndex}
                aria-label={`Go to slide ${i + 1} of ${slides.length}`}
                onClick={() => {
                  // Mark engagement so auto-advance pauses; jump directly.
                  setEngageUntil(Date.now() + ENGAGE_PAUSE_MS);
                  setActiveIndex(i);
                }}
                className={`stats-carousel-pip ${i === activeIndex ? 'is-active' : ''}`}
                data-testid={`${testIdPrefix}-pip-${i}`}
              />
            ))}
          </span>
          <p className="stats-carousel-counter" aria-hidden="true" data-testid={`${testIdPrefix}-counter`}>
            <span className="stats-carousel-counter-num">{activeIndex + 1}</span>
            <span className="stats-carousel-counter-sep"> of </span>
            <span className="stats-carousel-counter-total">{slides.length}</span>
          </p>
          <p className="stats-reel-hint" aria-hidden="true">
            Tap a dot to jump · tap or swipe the slide
          </p>
        </>
      )}
    </div>
  );
};

export default StatsReel;
